import { createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useMessageDrafts as useMessageDraftsHook } from '../hooks/useMessageDrafts';

const MessageDraftsContext = createContext();

/**
 * Custom hook to easily access the message drafts context.
 * Returns null if used outside a provider (e.g., in NewChatView).
 */
export const useMessageDraftsContext = () => {
  const context = useContext(MessageDraftsContext);
  return context || null;
};

/**
 * Provider component that encapsulates the message draft logic for a specific room.
 * It manages the message text state and provides all draft-related state and functions.
 */
export const MessageDraftsProvider = ({ roomId, children }) => {
  const [currentMessage, setCurrentMessage] = useState('');

  // useCallback ensures handleSetMessage has a stable identity
  const handleSetMessage = useCallback((text) => {
    setCurrentMessage(text);
  }, []);

  // The core hook that manages draft state
  const draftState = useMessageDraftsHook(roomId, currentMessage, handleSetMessage);

  // The value provided to consuming components
  const value = {
    ...draftState,
    currentMessage,
    onSetMessage: handleSetMessage,
  };

  return (
    <MessageDraftsContext.Provider value={value}>
      {children}
    </MessageDraftsContext.Provider>
  );
};

MessageDraftsProvider.propTypes = {
  roomId: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};